<template>
  <div class="cg-alert" :style="styleVariables">
    <div v-if="!hideIcon" class="cg-alert__icon">
      <!-- @slot Slot for icon - replaces default icon from alert type -->
      <slot name="icon">
        <cg-icon-warning
          v-if="showWarningIcon"
          :width="20"
          :height="20"
          :color="type === 'error' ? '#444' : '#fff'"
        />

        <cg-icon-info-circle
          v-if="showInfoIcon"
          :width="20"
          :height="20"
        />

        <cg-icon-check-circle
          v-if="showCheckIcon"
          :width="20"
          :height="20"
          :color="type === 'success' ? '#42B77A' : '#fff'"
          :strokeColor="type === 'success' ? '#fff' : '#42B77A'"
        />
      </slot>
    </div>
    <div class="cg-alert__content">
      <!-- @slot The default Vue slot. -->
      <slot />
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import { Colors } from '../../utils/Colors';

import { AlertType } from '../../types';

// @ts-ignore
import { CgIconWarning, CgIconInfoCircle, CgIconCheckCircle } from '../../icons';

export default Vue.extend({
  name: 'CgAlert',
  components: {
    CgIconWarning,
    CgIconInfoCircle,
    CgIconCheckCircle,
  },
  props: {
    /**
     * Specify alert type. Uses the contextual color and has a pre-defined icon.
     * @values success | error | information | warning | success-bold | error-bold
     */
    type: {
      type: String as PropType<AlertType>,
      required: false,
      default: AlertType.Success,
    },
    /**
     * Applies specified background color.
     */
    backgroundColor: {
      type: String as PropType<Colors | String>,
      required: false,
      default: null,
    },
    /**
     * Hides icon slot.
     */
    hideIcon: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    styleVariables(): Object {
      const { type, backgroundColor } = this;

      const typeBackgroundDictionary = new Map([
        [AlertType.Success, Colors.Green100],
        [AlertType.Error, Colors.Pink100],
        [AlertType.Information, Colors.Teal200],
        [AlertType.Warning, Colors.Yellow200],
        [AlertType.SuccessBold, Colors.Green400],
        [AlertType.ErrorBold, Colors.Pink500],
      ]);

      const typeColorDictionary = new Map([
        [AlertType.Success, Colors.Grey600],
        [AlertType.Error, Colors.Grey600],
        [AlertType.Information, Colors.Grey600],
        [AlertType.Warning, Colors.Grey600],
        [AlertType.SuccessBold, Colors.White],
        [AlertType.ErrorBold, Colors.White],
      ]);

      const typeBackgroundColor = typeBackgroundDictionary.get(type) || null;
      const typeColor = typeColorDictionary.get(type) || null;

      return {
        '--alert-background-color': backgroundColor || typeBackgroundColor,
        '--alert-color': typeColor,
      };
    },
    showWarningIcon(): boolean {
      return [AlertType.Error, AlertType.ErrorBold].includes(this.type)
    },
    showInfoIcon(): boolean {
      return [AlertType.Warning, AlertType.Information].includes(this.type)
    },
    showCheckIcon(): boolean {
      return [AlertType.Success, AlertType.SuccessBold].includes(this.type)
    }
  },
})
</script>

<style lang="scss" scoped>
@import './styles.scss';
</style>
